import { useMemo } from 'react';

import { URLS, createJurisdictionHeaders } from '@/data';
import { MarketCategorySummary, Sport } from '@/feature/event-details-sbk/types';
import { useIsSweepsProduct } from '@/hooks/use-is-sweeps-product';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { cacheControlHeader } from '@/utils/constants-platform-specific';
import { useQuery } from '@tanstack/react-query';

export const createLeagueKeys = (product: Product) => {
    const all = [product, 'league'] as const;
    return {
        all,
        getLeague: (leagueId: string) => [...all, { leagueId }] as const,
    };
};

export type LeagueKeys = ReturnType<typeof createLeagueKeys>;

export const useLeagueKeys = (): LeagueKeys => {
    const product = useJurisdictionStore(state => state.product);
    return useMemo(() => createLeagueKeys(product), [product]);
};

export const getLeagueKeys = (): LeagueKeys => createLeagueKeys(useJurisdictionStore.getState().product);

export type League = {
    id: string;
    name: string;
    logo_url: string;
    market_categories: MarketCategorySummary;
    sport: Sport;
    event_count: number;
    live_event_count: number;
};

export type GetLeagueResponse = {
    data: League;
};

export const getLeague = async (leagueId: string): Promise<GetLeagueResponse> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/leagues/${leagueId}`, {
        method: 'GET',
        headers: {
            ...createJurisdictionHeaders(),
            ...cacheControlHeader,
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw data;
    }
    return data;
};

// Filter out player prop market categories for the sweeps product
// We are filtering on the FE instead of changing huddle's configuration as it may affect other products
const filterMarketCategories = (league: League, isSweepsProduct: boolean) => {
    if (isSweepsProduct) {
        return {
            ...league,
            market_categories: league.market_categories.filter(category => category.type !== 'player_props'),
        };
    }
    return league;
};

export const useLeague = (leagueId: string) => {
    const isSweepsProduct = useIsSweepsProduct();
    const leagueKeys = useLeagueKeys();
    return useQuery({
        queryKey: leagueKeys.getLeague(leagueId),
        queryFn: () => getLeague(leagueId),
        select: data => filterMarketCategories(data.data, isSweepsProduct),
    });
};
